'use client';

import { useDeviceType } from '@packages/shared/src/hooks/useDeviceType/useDeviceType';
import { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { BypassBlock } from '@packages/shared/src/ui/BypassBlock/BypassBlock';
import { Link } from '@packages/modules/src/Link/Link';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@packages/shared/src/ui/Collapsible/Collapsible';
import type { Links, SeoContentProps } from './types';
import { useAssignLinkTargetBasedOnBotStatus } from '../../hooks/useAssignLinkTargetBasedOnBotStatus';
import { classes } from '../../utils/classes';

const shoppingTipsMessages = defineMessages({
  skip: {
    id: 'seoContent.shoppingtips.skip',
    defaultMessage: 'Shopping Tipps überspringen',
  },
  headline: {
    id: 'seoContent.shoppingtips.heading',
    defaultMessage: 'Shopping Tipps',
  },
  showMore: {
    id: 'seoContent.shoppingtips.more',
    defaultMessage: '... mehr anzeigen',
  },
  showLess: {
    id: 'seoContent.shoppingtips.less',
    defaultMessage: '... weniger anzeigen',
  },
});

const gridStyle =
  'xs:grid-cols-1 grid items-start justify-between gap-[3.5rem] sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-6';

const LinkGroups = ({ groups }: { groups: Links[] }) => {
  const assignLinkTarget = useAssignLinkTargetBasedOnBotStatus();
  return groups.map((group, index) => (
    // eslint-disable-next-line react/no-array-index-key
    <div key={index}>
      {group.links.map((link) => {
        const finalLink = assignLinkTarget(link.url);
        return (
          <Link
            className="text-size-body-xs mb-2 block underline"
            key={finalLink}
            href={finalLink}
            color="inherit"
          >
            {link.alt}
          </Link>
        );
      })}
    </div>
  ));
};

const maxGroupsMobile = 2;

/**
 * Renders SEO relevant content ("shopping tips") consisting of links to
 * product lists.
 *
 * On mobile devices only the first one or two columns are shown and the others can
 * be opened by clicking on the button "..show more"
 */
export const SeoContent = ({ seoContent }: SeoContentProps) => {
  const { formatMessage } = useIntl();
  const { isDesktop } = useDeviceType();

  const { linkGroups } = seoContent;
  const linkGroupsVisible = isDesktop ? linkGroups : linkGroups.slice(0, maxGroupsMobile);
  const linkGroupsCollapsed = isDesktop ? [] : linkGroups.slice(maxGroupsMobile, linkGroups.length);

  // state of the collapse
  const [opened, setOpened] = useState(false);

  return (
    <div className="px-3 pt-[3.5rem] lg:px-0">
      <p className="text-size-headline-xl mb-2" role="heading" aria-level={4}>
        {formatMessage(shoppingTipsMessages.headline)}
      </p>
      <BypassBlock buttonText={formatMessage(shoppingTipsMessages.skip)}>
        {isDesktop ? (
          <div className={gridStyle}>
            <LinkGroups groups={linkGroupsVisible} />
          </div>
        ) : (
          <Collapsible
            onOpenChange={() => setOpened((prev) => !prev)}
            open={isDesktop ? true : opened}
          >
            <div className={gridStyle}>
              <LinkGroups groups={linkGroupsVisible} />
            </div>
            <CollapsibleContent className={classes(gridStyle, opened && 'mt-[3.5rem]')}>
              <LinkGroups groups={linkGroupsCollapsed} />
            </CollapsibleContent>

            <CollapsibleTrigger className="pointer mt-2 mb-[-3.5rem] underline">
              {formatMessage(
                opened ? shoppingTipsMessages.showLess : shoppingTipsMessages.showMore,
              )}
            </CollapsibleTrigger>
          </Collapsible>
        )}
      </BypassBlock>
    </div>
  );
};
